import React, { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Square, Clock, Play, Trash2 } from "lucide-react";

const fmtDur = (s) => {
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};

export default function TaskTimeTracking({ task, canManage, currentUser, refreshActivity }) {
  const [timeData, setTimeData] = useState({ entries: [], total_seconds: 0, running: null });
  const [tmH, setTmH] = useState("");
  const [tmM, setTmM] = useState("");
  const [tmNote, setTmNote] = useState("");
  const [nowTick, setNowTick] = useState(Date.now());

  const loadTime = useCallback(() => { if (task) api.get(`/tasks/${task.task_id}/time`).then((r) => setTimeData(r.data)).catch(() => {}); }, [task]);

  useEffect(() => { loadTime(); }, [loadTime]);

  useEffect(() => {
    if (!timeData.running) return;
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [timeData.running]);

  const startTimer = async () => {
    try { await api.post(`/tasks/${task.task_id}/time/start`); loadTime(); }
    catch (e) { toast.error(apiErr(e, "Could not start timer")); }
  };
  const stopTimer = async () => {
    try { await api.post(`/tasks/${task.task_id}/time/stop`); loadTime(); refreshActivity(); }
    catch (e) { toast.error("Could not stop timer"); }
  };
  const addManualTime = async () => {
    const secs = (parseInt(tmH || 0, 10) * 3600) + (parseInt(tmM || 0, 10) * 60);
    if (secs <= 0) { toast.error("Enter hours or minutes"); return; }
    try { await api.post(`/tasks/${task.task_id}/time`, { seconds: secs, note: tmNote }); setTmH(""); setTmM(""); setTmNote(""); loadTime(); refreshActivity(); }
    catch (e) { toast.error("Could not add time"); }
  };
  const deleteTime = async (id) => {
    try { await api.delete(`/time/${id}`); loadTime(); }
    catch (e) { toast.error("Could not delete entry"); }
  };

  return (
    <div data-testid="time-tracking-section">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" /> Time Tracking
        </label>
        <span className="text-xs font-bold text-zinc-900" data-testid="time-total">{fmtDur(timeData.total_seconds)}</span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        {timeData.running ? (
          <button onClick={stopTimer} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700" data-testid="timer-stop">
            <Square className="h-3 w-3 fill-white" /> Stop {fmtDur(Math.max(0, Math.floor((nowTick - Date.parse(timeData.running.started_at)) / 1000)))}
          </button>
        ) : (
          <button onClick={startTimer} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-zinc-900 text-white text-xs font-semibold hover:bg-zinc-800" data-testid="timer-start">
            <Play className="h-3 w-3 fill-white" /> Start timer
          </button>
        )}
      </div>
      <div className="flex items-center gap-1.5 mb-2">
        <input type="number" min="0" value={tmH} onChange={(e) => setTmH(e.target.value)} placeholder="h" className="w-12 h-8 border border-zinc-200 rounded-sm px-2 text-sm" data-testid="time-hours" />
        <input type="number" min="0" max="59" value={tmM} onChange={(e) => setTmM(e.target.value)} placeholder="m" className="w-12 h-8 border border-zinc-200 rounded-sm px-2 text-sm" data-testid="time-mins" />
        <input value={tmNote} onChange={(e) => setTmNote(e.target.value)} placeholder="Note (optional)" className="flex-1 min-w-0 h-8 border border-zinc-200 rounded-sm px-2 text-sm" data-testid="time-note" />
        <button onClick={addManualTime} className="h-8 px-3 rounded-sm border border-zinc-300 text-xs font-semibold hover:bg-zinc-50" data-testid="time-add">Add</button>
      </div>
      <div className="space-y-1">
        {timeData.entries.length === 0 && !timeData.running && <p className="text-xs text-zinc-400">No time logged yet.</p>}
        {timeData.entries.map((e) => (
          <div key={e.entry_id} className="flex items-center gap-2 text-xs text-zinc-600 border border-zinc-100 rounded-sm px-2 py-1.5" data-testid={`time-entry-${e.entry_id}`}>
            <span className="font-semibold text-zinc-900 w-14 shrink-0">{fmtDur(e.seconds)}</span>
            <span className="text-zinc-400 shrink-0">{e.user_name}</span>
            {e.note && <span className="truncate flex-1">— {e.note}</span>}
            <span className="text-zinc-300 ml-auto shrink-0">{e.date}{e.manual ? "" : " ·⏱"}</span>
            {(e.user_id === currentUser?.user_id || canManage) && (
              <button onClick={() => deleteTime(e.entry_id)} className="text-zinc-400 hover:text-red-600 shrink-0"><Trash2 className="h-3 w-3" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
